import { HistoryItemPeriod } from "../../interfaces/historyItem";
export interface ISelectionRange {
    firstSelected: number;
    lastSelected: number;
    firstVisible: number;
    lastVisible: number;
}

export enum ChartAnnotationTool {
    None = "none",
    Line = "line",
    HorizontalLine = "horizontal-line",
    VerticalLine = "vertical-line",
    InfiniteLine = "infinite-line",
    Ray = "ray",
    Triangle = "triangle",
    Rectangle = "rectangle",
    Ellipse = "ellipse",
    TrendChannel = "trend-channel",
    ThreeLinesTrendChannel = "three-lines-trend-channel",
    AndrewsPitchfork = "andrews-pitchfork",
    FibonacciFan = "fibonacci-fan",
    FibonacciArc = "fibonacci-arc",
    FibonacciRetracement = "fibonacci-retracement",
    FibonacciTimezones = "fibonacci-timezones",
    Marker = "marker",
    Label="label"
}

export enum ChartStokeStyle {
    Solid = "solid",
    Doted = "doted",
    Dashed = "dashed"
}

export enum ChartMarkerType {
    None = "none",
    ArrowUp = "arrowUp",
    ArrowDown = "arrow-down",
    ArrowLeft = "arrow-left",
    ArrowRight = "arrow-right",
    ArrowHead = "arrowHead",
    Cross = "cross",
    DiagonalCross = "diagonal-cross",
    Diamond = "diamond",
    Pentagon = "pentagon",
    Square = "square",
    Star10 = "star10",
    Star4 = "star4",
    Star5 = "star5",
    Star6 = "star6",
    Star7 = "star7",
    Trapezium = "trapezium",
    TriangleUp = "triangle-up",
    TriangleDown = "triangle-down",
    TriangleLeft = "triangle-left",
    TriangleRight = "triangle-right"
}

export enum ChartMarkerAnchor {
    None = "none",
    CenterTop = "center-top",
    CenterBottom = "center-bottom",
    LeftCenter = "left-center",
    RightCenter = "right-center",
    RightTop = "right-top",
    RightBottom = "right-bottom",
    Center = "center",
    LeftTop = "left-top",
    LeftBottom = "left-bottom",
    Auto = "auto"
}

export interface IMarkerAnchorMap {
    marker: ChartMarkerType;
    anchor: ChartMarkerAnchor;
}

export class MarkerAnchorMap {
    static getAnchor(marker: ChartMarkerType): ChartMarkerAnchor {
        let ma: IMarkerAnchorMap[] = MarkerAnchorMap.markerAnchorMap.filter(x => x.marker === marker);
        return ma.length > 0 ? ma[0].anchor : ChartMarkerAnchor.None;
    }

    static markerAnchorMap: IMarkerAnchorMap[] = [
        {
            marker: ChartMarkerType.ArrowUp,
            anchor: ChartMarkerAnchor.CenterTop
        },
        {
            marker: ChartMarkerType.ArrowDown,
            anchor: ChartMarkerAnchor.CenterBottom
        },
        {
            marker: ChartMarkerType.ArrowLeft,
            anchor: ChartMarkerAnchor.LeftCenter
        },
        {
            marker: ChartMarkerType.ArrowHead,
            anchor: ChartMarkerAnchor.RightCenter
        },
        {
            marker: ChartMarkerType.Cross,
            anchor: ChartMarkerAnchor.Center
        },
        {
            marker: ChartMarkerType.DiagonalCross,
            anchor: ChartMarkerAnchor.Center
        },
        {
            marker: ChartMarkerType.Diamond,
            anchor: ChartMarkerAnchor.Center
        },
        {
            marker: ChartMarkerType.Pentagon,
            anchor: ChartMarkerAnchor.Center
        },
        {
            marker: ChartMarkerType.Square,
            anchor: ChartMarkerAnchor.Center
        },
        {
            marker: ChartMarkerType.Star10,
            anchor: ChartMarkerAnchor.Center
        },
        {
            marker: ChartMarkerType.Star4,
            anchor: ChartMarkerAnchor.Center
        },
        {
            marker: ChartMarkerType.Star5,
            anchor: ChartMarkerAnchor.Center
        },
        {
            marker: ChartMarkerType.Star6,
            anchor: ChartMarkerAnchor.Center
        },
        {
            marker: ChartMarkerType.Star7,
            anchor: ChartMarkerAnchor.Center
        },
        {
            marker: ChartMarkerType.Trapezium,
            anchor: ChartMarkerAnchor.Center
        },
        {
            marker: ChartMarkerType.TriangleUp,
            anchor: ChartMarkerAnchor.CenterTop
        },
        {
            marker: ChartMarkerType.TriangleDown,
            anchor: ChartMarkerAnchor.CenterBottom
        },
        {
            marker: ChartMarkerType.TriangleLeft,
            anchor: ChartMarkerAnchor.LeftCenter
        },
        {
            marker: ChartMarkerType.TriangleRight,
            anchor: ChartMarkerAnchor.RightCenter
        }
    ];
}

export enum ChartTypes {
    AREA = "area",
    VERTICAL_AREA = "vertical-area",
    AREA_3D = "area-3d",
    BAR = "bar",
    BAR_3D = "bar-3d",
    BOX = "box",
    BUBBLE = "bubble",
    BULLET = "bullet",
    CARTESIAN = "cartesian",
    CARTESIAN_3D = "cartesian-3d",
    COLUMN = "column",
    COLUMN_3D = "column-3d",
    FINANCIAL = "financial",
    FUNNEL = "funnel",
    LINE = "line",
    LINE_3D = "line-3d",
    VERTICAL_LINE = "vertical-line",
    MARKER = "marker",
    PIE = "pie",
    PIE_3D = "pie-3d",
    POLAR = "polar",
    PYRAMID = "pyramid",
    RADAR = "radar",
    SCATTER = "scatter",
    SPARKLINE = "sparkline",
    HEAT_MAP = "heat-map",
    TREE_MAP = "tree-map",
    STOCK = "stock",
    PERT = "pert",
    GANTT_RESOURCE = "gantt-resource",
    GANTT_PROJECT = "gantt-project",
    RESOURCE = "resource",
    JUMP_LINE = "jump-line",
    STICK = "stick",
    PARETO = "pareto",
    QUADRANT = "quadrant",
    MEKKO = "mekko",
    MOSAIC = "mosaic",
    BARMEKKO = "barmekko",
    TAG_CLOUD = "tag-cloud",
    VENN = "venn",
    HILO = "hilo",
    WATERFALL = "waterfall",
    SUNBURST = "sunburst"
}

export enum Position {
    LEFT_TOP = "left-top",
    LEFT_CENTER = "left-center",
    LEFT_BOTTOM = "left-bottom",
    CENTER_TOP = "center-top",
    CENTER = "center",
    CENTER_BOTTOM = "center-bottom",
    RIGHT_TOP = "right-top",
    RIGHT_CENTER = "right-center",
    RIGHT_BOTTOM = "right-bottom"
}

export enum ChartScrollerPosition {
    BEFORE_AXES = "before-axes",
    AFTER_AXES = "after-axes"
}

export enum Align {
    CENTER = "center",
    LEFT = "left",
    RIGHT = "right",
    TOP = "top",
    BOTTOM = "bottom"
}

export enum Layout {
    VERTICAL = "vertical",
    HORIZONTAL = "horizontal"
}

export enum LegendLayout {
    VERTICAL = "vertical",
    HORIZONTAL = "horizontal",
    VERTICAL_EXPANDABLE = "vertical-expandable",
    HORIZONTAL_EXPANDABLE = "horizontal-expandable"
}

export enum Orientation {
    LEFT = "left",
    RIGHT = "right",
    TOP = "top",
    BOTTOM = "bottom"
}

export enum LegendPositionMode {
    INSIDE = "inside",
    OUTSIDE = "outside"
}

export enum EventMarkerType {
    CIRCLE = "circle",
    RECT = "rect",
    PIN = "pin",
    FLAG = "flag"
}

export enum EventMarkerDirection {
    UP = "up",
    DOWN = "down",
    AUTO = "auto"
}

export enum EventMarkerPosition {
    SERIES = "series",
    SERIES_POSITIVE = "series-positive",
    SERIES_NEGATIVE = "series-negative",
    ZERO = "zero",
    AXIS = "axis"
}

export enum MapAsTableMode {
    /**
     * Single values.
     * Means, that each series is represented by one column in the table + shared X column (own Y values).
     */
    VALUE = "value",
    /**
     * High-Low values.
     * Means, that each series is represented by two columns per series + shared X.
     */
    RANGE = "range",
    /**
     * Open-High-Low-Close values.
     * Means, that each series is represented by four columns per series + shared X.
     */
    OHLC = "ohlc"
}

export enum LegendItemIconType {
    // icons by series type
    AREA = "area",
    BAR = "bar",
    BUBBLE = "bubble",
    CANDLESTICK = "candlestick",
    COLUMN = "column",
    LINE = "line",
    OHLC = "ohlc",
    RANGE_AREA = "range-area",
    RANGE_BAR = "range-bar",
    RANGE_COLUMN = "range-column",
    RANGE_SPLINE_AREA = "range-spline-area",
    RANGE_STEP_AREA = "range-step-area",
    SPLINE = "spline",
    SPLINE_AREA = "spline-area",
    STEP_LINE = "step-line",
    STEP_AREA = "step-area",
    CIRCLE = "circle",
    SQUARE = "square",
    // icons by marker type
    TRIANGLE_UP = "triangle-up",
    TRIANGLE_DOWN = "triangle-down",
    DIAMOND = "diamond",
    CROSS = "cross",
    DIAGONAL_CROSS = "diagonal-cross",
    STAR4 = "star4",
    STAR5 = "star5",
    STAR6 = "star6",
    STAR7 = "star7",
    STAR10 = "star10",
    PENTAGON = "pentagon",
    TRAPEZIUM = "trapezium",
    ARROWHEAD = "arrowhead",
    V_LINE = "v-line",
    // special icon types
    MARKER = "marker",
    RISING_FALLING = "rising-falling"
}

export enum EventType {
    POINT_MOUSE_OUT = "pointmouseout",
    POINT_MOUSE_OVER = "pointMouseOver",
    POINT_MOUSE_MOVE = "pointmousemove",
    POINT_MOUSE_DOWN = "pointmousedown",
    POINT_MOUSE_UP = "pointmouseup",
    POINT_CLICK = "pointclick",
    POINT_DBLCLICK = "pointdblclick",
    // deprecated
    POINT_HOVER = "pointhover",
    POINTS_SELECT = "pointsselect",
    POINTS_HOVER = "pointshover",
    CHART_DRAW = "chartdraw",
    ANIMATION_START = "animationstart",
    ANIMATION_END = "animationend",
    DRILL_CHANGE = "drillchange",

    ZOOM_START = "zoomstart",
    ZOOM = "zoom",
    ZOOM_END = "zoomend",

    LEGEND_ITEM_MOUSE_OUT = "legenditemmouseout",
    LEGEND_ITEM_MOUSE_OVER = "legenditemmouseover",
    LEGEND_ITEM_MOUSE_MOVE = "legenditemmousemove",
    LEGEND_ITEM_MOUSE_DOWN = "legenditemmousedown",
    LEGEND_ITEM_MOUSE_UP = "legenditemmouseup",
    LEGEND_ITEM_CLICK = "legenditemclick",
    LEGEND_ITEM_DBLCLICK = "legenditemdblclick",
    EVENT_MARKER_MOUSE_OUT = "eventmarkermouseout",
    EVENT_MARKER_MOUSE_OVER = "eventmarkermouseover",
    EVENT_MARKER_MOUSE_MOVE = "eventmarkermousemove",
    EVENT_MARKER_MOUSE_DOWN = "eventmarkermousedown",
    EVENT_MARKER_MOUSE_UP = "eventmarkermouseup",
    EVENT_MARKER_CLICK = "eventmarkerclick",
    EVENT_MARKER_DBLCLICK = "eventmarkerdblclick",
    EVENT_MARKERS_HOVER = "eventmarkershover",
    EVENT_MARKERS_SELECT = "eventmarkersselect",
    DRAG = "drag",
    DRAG_START = "dragstart",
    DRAG_END = "dragend",
    SCROLL_CHANGE = "scrollchange",
    SCROLLING = "scrolling",
    SCROLL_END = "scrollend",
    SPLITTER_CHANGE = "splitterchange",
    SCROLLER_CHANGE_START = "scrollerchangestart",
    SCROLLER_CHANGE = "scrollerchange",
    SCROLLER_CHANGE_FINISH = "scrollerchangefinish",
    SELECTED_RANGE_CHANGE_START = "selectedrangechangestart",
    SELECTED_RANGE_BEFORE_CHANGE = "selectedrangebeforechange",
    SELECTED_RANGE_CHANGE = "selectedrangechange",
    SELECTED_RANGE_CHANGE_FINISH = "selectedrangechangefinish",

    HIGHLIGHT = "highlight",
    UNHIGHLIGHT = "unhighlight",
    SIGNAL = "signal",

    // grid events.
    ROW_SELECT = "rowSelect",
    ROW_CLICK = "rowClick",
    ROW_DBL_CLICK = "rowDblClick",
    ROW_MOUSE_OVER = "rowMouseOver",
    ROW_MOUSE_OUT = "rowMouseOut",
    ROW_MOUSE_MOVE = "rowMouseMove",
    ROW_MOUSE_DOWN = "rowMouseDown",
    ROW_MOUSE_UP = "rowMouseUp",
    BEFORE_CREATE_CONNECTOR = "beforeCreateConnector",
    ROW_COLLAPSE_EXPAND = "rowcollapseexpand",

    // connectors events.
    CONNECTOR_SELECT = "connectorselect",
    CONNECTOR_CLICK = "connectorclick",
    CONNECTOR_DBL_CLICK = "connectordblclick",
    CONNECTOR_MOUSE_OVER = "connectormouseover",
    CONNECTOR_MOUSE_OUT = "connectormouseout",
    CONNECTOR_MOUSE_MOVE = "connectormousemove",
    CONNECTOR_MOUSE_DOWN = "connectormousedown",
    CONNECTOR_MOUSE_UP = "connectormouseup",

    // data tree CRUD events.
    TREE_ITEM_MOVE = "treeItemMove",
    TREE_ITEM_UPDATE = "treeItemUpdate",
    TREE_ITEM_CREATE = "treeItemCreate",
    TREE_ITEM_REMOVE = "treeItemRemove",

    // annotation events
    ANNOTATION_SELECT = "annotationSelect",
    ANNOTATION_UNSELECT = "annotationUnselect",
    ANNOTATION_DRAWING_FINISH = "annotationDrawingFinish",
    ANNOTATION_CHANGE_START = "annotationChangeStart",
    ANNOTATION_CHANGE = "annotationChange",
    ANNOTATION_CHANGE_FINISH = "annotationChangeFinish",

    // ui events
    CLOSE = "close",
    COMPLETE = "complete",

    SELECT_MARQUEE_START = "selectmarqueestart",
    SELECT_MARQUEE_CHANGE = "selectmarqueechange",
    SELECT_MARQUEE_FINISH = "selectmarqueefinish",

    // data changed event for no data label
    DATA_CHANGED = "datachanged",

    // edit input ui events
    EDIT_INPUT_BEFORE_SHOW = "editinputbeforeshow",
    EDIT_INPUT_SHOW = "editinputshow",
    EDIT_INPUT_SUBMIT = "editinputsubmit",
    EDIT_INPUT_ESCAPE = "editinputescape",
    EDIT_INPUT_BLUR = "editinputblur",
    EDIT_INPUT_FOCUS = "editinputfocus",
    EDIT_INPUT_KEY_PRESS = "editinputkeypress",
    EDIT_INPUT_BEFORE_HIDE = "editinputbeforehide",
    EDIT_INPUT_HIDE = "editinputhide"
}

export enum StockSeriesType {
    AREA = "area",
    // aBAR= 'bar',
    // aBOX= 'box',
    // aBUBBLE= 'bubble',
    CANDLESTICK = "candlestick",
    COLUMN = "column",
    LINE = "line",
    MARKER = "marker",
    OHLC = "ohlc",
    RANGE_AREA = "range-area",
    // aRANGE_BAR= 'range-bar',
    RANGE_COLUMN = "range-column",
    RANGE_SPLINE_AREA = "range-spline-area",
    RANGE_STEP_AREA = "range-step-area",
    SPLINE = "spline",
    SPLINE_AREA = "spline-area",
    STEP_AREA = "step-area",
    STEP_LINE = "step-line",
    JUMP_LINE = "jump-line",
    STICK = "stick",
    HILO = "hilo",

    // custom
    PRICEPOINTLINE="marker,line"
}

export enum StockIndicatorTypes {
    ADL = "adl",
    AMA = "ama",
    AROON = "aroon",
    ATR = "atr",
    BBANDS = "bbands",
    BBANDS_B = "bbands-b",
    BBANDS_WIDTH = "bbands-width",
    CCI = "cci",
    CHO = "cho",
    CMF = "cmf",
    DMI = "dmi",
    EMA = "ema",
    KDJ = "kdj",
    MACD = "macd",
    MFI = "mfi",
    MMA = "mma",
    MOMENTUM = "momentum",
    PSAR = "psar",
    ROC = "roc",
    RSI = "rsi",
    SMA = "sma",
    STOCHASTIC = "stochastic",
    WILLIAMS_R = "williams-r"
}

export enum ScaleTypes {
    LINEAR = "linear",
    LOG = "log",
    DATE_TIME = "date-time",
    DATE_TIME_WITH_CALENDAR = "date-time-with-calendar",
    ORDINAL = "ordinal",
    ORDINAL_COLOR = "ordinal-color",
    LINEAR_COLOR = "linear-color",
    STOCK_SCATTER_DATE_TIME = "stock-scatter-date-time",
    STOCK_ORDINAL_DATE_TIME = "stock-ordinal-date-time",
    GANTT = "gantt"
}

export enum Interval {
    YEAR = "year",
    SEMESTER = "semester",
    QUARTER = "quarter",
    MONTH = "month",
    THIRD_OF_MONTH = "third-of-month",
    WEEK = "week",
    DAY = "day",
    HOUR = "hour",
    MINUTE = "minute",
    SECOND = "second",
    MILLISECOND = "millisecond"
}

export enum TextDecoration {
    /** Blinking text. This value is not supported by some browser and is discussed in CSS3, animation is recommended instead. */
    BLINK = "blink",
    /** Line through decoration. */
    LINE_THROUGH = "line-through",
    /** Overline decoration. */
    OVERLINE = "overline",
    /** Underline decoration. */
    UNDERLINE = "underline",
    /** Cancels all decorations, including links underline. */
    NONE = "none"
}

export enum FontStyle {
    NORMAL = "normal",
    ITALIC = "italic",
    OBLIQUE = "oblique"
}

export enum FontVariant {
    NORMAL = "normal",
    SMALL_CAP = "small-caps"
}

export enum WordBreak {
    NORMAL = "normal",
    KEEP_ALL = "keep-all",
    BREAK_ALL = "break-all"
}

export enum WordWrap {
    NORMAL = "normal",
    BREAK_WORD = "break-word"
}

export enum StockRangeChangeSource {
    SCROLLER_THUMB_DRAG = "scroller-thumb-drag",
    SCROLLER_DRAG = "scroller-drag",
    SCROLLER_CLICK = "scroller-click",
    PLOT_DRAG = "plot-drag",
    DATA_CHANGE = "data-update",
    SELECT_RANGE = "select-range",
    MARQUEE = "marquee",
    MOUSE_WHEEL = "mouse-wheel"
}

export enum StockRangeType {
    UNIT = "unit",
    POINTS = "points",
    RANGE = "range",
    YTD = "ytd",
    QTD = "qtd",
    MTD = "mtd",
    MAX = "max"
}

export enum CrosshairDisplayMode {
    FLOAT = "float",
    STICKY = "sticky"
}

export enum ColorType {
    FILL = 1,
    STROKE = 2,
    HATCH_FILL = 3
}

export enum LocaleDateTimeFormat {
    YEAR = "year",
    YEAR_SEMESTER = "year-semester",
    YEAR_QUARTER = "year-quarter",
    YEAR_MONTH = "year-month",
    YEAR_THIRD_OF_MONTH = "year-third-of-month",
    YEAR_WEEK = "year-week",
    YEAR_DAY = "year-day",
    YEAR_HOUR = "year-hour",
    YEAR_MINUTE = "year-minute",
    YEAR_SECOND = "year-second",
    YEAR_MILLISECOND = "year-millisecond",
    SEMESTER = "semester",
    SEMESTER_QUARTER = "semester-quarter",
    SEMESTER_MONTH = "semester-month",
    SEMESTER_THIRD_OF_MONTH = "semester-third-of-month",
    SEMESTER_WEEK = "semester-week",
    SEMESTER_DAY = "semester-day",
    SEMESTER_HOUR = "semester-hour",
    SEMESTER_MINUTE = "semester-minute",
    SEMESTER_SECOND = "semester-second",
    SEMESTER_MILLISECOND = "semester-millisecond",
    QUARTER = "quarter",
    QUARTER_MONTH = "quarter-month",
    QUARTER_THIRD_OF_MONTH = "quarter-third-of-month",
    QUARTER_WEEK = "quarter-week",
    QUARTER_DAY = "quarter-day",
    QUARTER_HOUR = "quarter-hour",
    QUARTER_MINUTE = "quarter-minute",
    QUARTER_SECOND = "quarter-second",
    QUARTER_MILLISECOND = "quarter-millisecond",
    MONTH = "month",
    MONTH_THIRD_OF_MONTH = "month-third-of-month",
    MONTH_WEEK = "month-week",
    MONTH_DAY = "month-day",
    MONTH_HOUR = "month-hour",
    MONTH_MINUTE = "month-minute",
    MONTH_SECOND = "month-second",
    MONTH_MILLISECOND = "month-millisecond",
    THIRD_OF_MONTH = "third-of-month",
    THIRD_OF_MONTH_WEEK = "third-of-month-week",
    THIRD_OF_MONTH_DAY = "third-of-month-day",
    THIRD_OF_MONTH_HOUR = "third-of-month-hour",
    THIRD_OF_MONTH_MINUTE = "third-of-month-minute",
    THIRD_OF_MONTH_SECOND = "third-of-month-second",
    THIRD_OF_MONTH_MILLISECOND = "third-of-month-millisecond",
    WEEK = "week",
    WEEK_DAY = "week-day",
    WEEK_HOUR = "week-hour",
    WEEK_MINUTE = "week-minute",
    WEEK_SECOND = "week-second",
    WEEK_MILLISECOND = "week-millisecond",
    DAY = "day",
    DAY_HOUR = "day-hour",
    DAY_MINUTE = "day-minute",
    DAY_SECOND = "day-second",
    DAY_MILLISECOND = "day-millisecond",
    HOUR = "hour",
    HOUR_MINUTE = "hour-minute",
    HOUR_SECOND = "hour-second",
    HOUR_MILLISECOND = "hour-millisecond",
    MINUTE = "minute",
    MINUTE_SECOND = "minute-second",
    MINUTE_MILLISECOND = "minute-millisecond",
    SECOND = "second",
    SECOND_MILLISECOND = "second-millisecond",
    MILLISECOND = "millisecond"
}

export interface IRangeSelection {
    type: StockRangeType;
    unit?: string;
    count?: number;
    text: string;
    period: HistoryItemPeriod;
    timeRange: number;
}

export var selectionRanges: IRangeSelection[] = [
    {
        type: StockRangeType.UNIT,
        unit: "Hour",
        count: 2,
        text: "2H",
        period: HistoryItemPeriod.Min1,
        timeRange: 2*60*60*1000
    },
    {
        type: StockRangeType.UNIT,
        unit: "Hour",
        count: 5,
        text: "5H",
        period: HistoryItemPeriod.Min3,
        timeRange: 5*60*60*1000
    },
    {
        type: StockRangeType.UNIT,
        unit: "Hour",
        count: 12,
        text: "12H",
        period: HistoryItemPeriod.Min5,
        timeRange: 12*60*60*1000
    },
    {
        type: StockRangeType.UNIT,
        unit: "Hour",
        count: 24,
        text: "24H",
        period: HistoryItemPeriod.Min5,
        timeRange: 24*60*60*1000
    },
    {
        type: StockRangeType.UNIT,
        unit: "Day",
        count: 1,
        text: "1D",
        period: HistoryItemPeriod.Min15,
        timeRange: 24*60*60*1000
    },
    {
        type: StockRangeType.UNIT,
        unit: "Day",
        count: 3,
        text: "3D",
        period: HistoryItemPeriod.Min30,
        timeRange: 3*24*60*60*1000
    },
    {
        type: StockRangeType.UNIT,
        unit: "Day",
        count: 5,
        text: "5D",
        period: HistoryItemPeriod.Hr1,
        timeRange: 5*24*60*60*1000
    },
    {
        type: StockRangeType.UNIT,
        unit: "Day",
        count: 15,
        text: "15D",
        period: HistoryItemPeriod.Hr1,
        timeRange: 15*24*60*60*1000
    },
    {
        type: StockRangeType.UNIT,
        unit: "Month",
        count: 1,
        text: "1M",
        period: HistoryItemPeriod.Hr24,
        timeRange: 30*24*60*60*1000
    },
    {
        type: StockRangeType.UNIT,
        unit: "Month",
        count: 3,
        text: "3M",
        period: HistoryItemPeriod.Hr24,
        timeRange: 3*30*24*60*60*1000
    },
    {
        type: StockRangeType.YTD,
        text: "YTD",
        period: HistoryItemPeriod.Week1,
        timeRange: 365*24*60*60*1000
    },
    {
        type: StockRangeType.UNIT,
        unit: "Year",
        count: 1,
        text: "1Y",
        period: HistoryItemPeriod.Week1,
        timeRange: 365*24*60*60*1000
    },
    {
        type: StockRangeType.UNIT,
        unit: "Year",
        count: 2,
        text: "2Y",
        period: HistoryItemPeriod.Week1,
        timeRange: 2*365*24*60*60*1000
    },
    {
        type: StockRangeType.UNIT,
        unit: "Year",
        count: 5,
        text: "5Y",
        period: HistoryItemPeriod.Mth1,
        timeRange: 5*365*24*60*60*1000
    },
    {
        type: StockRangeType.MAX,
        text: "MAX",
        period: HistoryItemPeriod.Mth1,
        timeRange: 10*365*24*60*60*1000
    }
];

